import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Separator } from "../ui/separator";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../ui/tabs";
import { 
  Building2, Globe, Truck, DollarSign, Mail, Phone, MapPin,
  Save, RefreshCw, Shield, Palette, CreditCard, Package,
  Settings2, Search, FileText, Image, ToggleLeft, Cloud, CheckCircle, XCircle
} from "lucide-react";
import { API } from "../../apiConfig";


const SiteSettingsTab = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("business");
  
  const [settings, setSettings] = useState({
    // Business Info
    business_name: "Art By Arawol",
    tagline: "Authentic Aboriginal Art",
    contact_email: "info@arawol.art",
    contact_phone: "+61 2 1234 5678",
    address: {
      street: "",
      city: "Sydney",
      state: "NSW",
      postcode: "2000",
      country: "Australia"
    },
    abn: "",
    
    // SEO Settings
    seo: {
      meta_title: "Art By Arawol | Authentic Aboriginal Art by Colin Isaacs",
      meta_description: "Discover authentic Aboriginal artwork by Colin Isaacs. Original paintings, limited edition prints, and certificates of authenticity.",
      og_image: "",
      keywords: "aboriginal art, indigenous art, colin isaacs, australian art, original paintings"
    },
    
    // Shipping Settings
    shipping: {
      free_shipping_threshold: 500,
      domestic_flat_rate: 15,
      express_rate: 35,
      international_enabled: true,
      international_rate: 75,
      handling_days: 2,
      shipping_origins: ["NSW", "Australia"]
    },
    
    // Tax Settings
    tax: {
      gst_enabled: true,
      gst_rate: 10,
      gst_inclusive: true,
      abn_exempt: true
    },
    
    // Currency & Regional
    regional: {
      currency: "AUD",
      currency_symbol: "$",
      timezone: "Australia/Sydney",
      date_format: "DD/MM/YYYY"
    },
    
    // Feature Toggles
    features: {
      wholesale_enabled: true,
      promoter_program_enabled: true,
      sales_agent_program_enabled: true,
      dynamic_pricing_enabled: true,
      certificates_enabled: true,
      ar_viewer_enabled: true,
      frame_configurator_enabled: true
    },
    
    // Cloud Storage (Cloudinary)
    cloudinary: {
      cloud_name: "",
      api_key: "",
      api_secret: ""
    },
    
    // Payment Settings (PayPal)
    paypal: {
      client_id: "",
      client_secret: "",
      sandbox_mode: true,
      pay_later_enabled: true
    },
    
    // Payment Settings (Stripe)
    stripe: {
      publishable_key: "",
      secret_key: "",
      test_mode: true
    }
  });

  const [cloudinaryStatus, setCloudinaryStatus] = useState(null);
  const [paypalStatus, setPaypalStatus] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchSettings();
    checkCloudinaryStatus();
    checkPaymentStatus();
    loadPaymentCredentials();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/site-settings`, { headers });
      if (res.data) {
        setSettings(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      console.log("Using default settings");
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentCredentials = async () => {
    try {
      const [stripeRes, paypalRes] = await Promise.all([
        axios.get(`${API}/admin/payment-config/stripe`, { headers }).catch(() => null),
        axios.get(`${API}/admin/payment-config/paypal`, { headers }).catch(() => null),
      ]);
      if (stripeRes?.data?.configured && stripeRes.data.value) {
        // Only load non-sensitive fields; leave key fields empty so user re-enters
        const sv = stripeRes.data.value;
        setSettings(prev => ({
          ...prev,
          stripe: { 
            ...prev.stripe, 
            test_mode: sv.test_mode ?? prev.stripe.test_mode,
            // Don't pre-fill masked keys - show as placeholder hint instead
            publishable_key: "",
            secret_key: "",
          }
        }));
      }
      if (paypalRes?.data?.configured && paypalRes.data.value) {
        const pv = paypalRes.data.value;
        setSettings(prev => ({
          ...prev,
          paypal: { 
            ...prev.paypal,
            sandbox: pv.sandbox ?? pv.sandbox_mode ?? prev.paypal.sandbox,
            pay_later_enabled: pv.pay_later_enabled ?? prev.paypal.pay_later_enabled,
            client_id: "",
            client_secret: "",
          }
        }));
      }
    } catch (error) {
      // Silently fail — fields stay empty
    }
  };

  const checkCloudinaryStatus = async () => {
    try {
      const res = await axios.get(`${API}/cloudinary/status`, { headers });
      setCloudinaryStatus(res.data);
    } catch (error) {
      setCloudinaryStatus({ configured: false });
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/payment-status`, { headers });
      setPaypalStatus(res.data.paypal);
      setStripeStatus(res.data.stripe);
    } catch (error) {
      setPaypalStatus({ configured: false });
      setStripeStatus({ configured: false });
    }
  };

  const savePaymentCredentials = async (provider) => {
    setSaving(true);
    try {
      const credentials = provider === 'paypal' ? settings.paypal : settings.stripe;
      // Only send non-empty fields to avoid overwriting existing keys with blanks
      const payload = {};
      for (const [k, v] of Object.entries(credentials)) {
        if (v !== "" && v !== null && v !== undefined) {
          payload[k] = v;
        }
      }
      await axios.post(`${API}/admin/payment-config/${provider}`, payload, { headers });
      toast.success(`${provider === 'paypal' ? 'PayPal' : 'Stripe'} credentials saved successfully`);
      checkPaymentStatus();
    } catch (error) {
      toast.error(`Failed to save ${provider} credentials`);
    } finally {
      setSaving(false);
    }
  };

  const testPayPalConnection = async () => {
    try {
      const res = await axios.post(`${API}/admin/test-paypal`, {}, { headers });
      if (res.data.success) {
        toast.success("PayPal connection successful!");
      } else {
        toast.error(res.data.error || "PayPal connection failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "PayPal connection test failed");
    }
  };

  const saveCloudinaryCredentials = async () => {
    setSaving(true);
    try {
      // Save to backend environment (this would need a secure endpoint)
      await axios.post(`${API}/admin/cloudinary-config`, settings.cloudinary, { headers });
      toast.success("Cloudinary credentials saved");
      checkCloudinaryStatus();
    } catch (error) {
      toast.error("Failed to save Cloudinary credentials");
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, settings, { headers });
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (path, value) => {
    setSettings(prev => {
      const keys = path.split(".");
      const newSettings = { ...prev };
      let current = newSettings;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-32 bg-muted rounded-2xl" />
        <div className="h-64 bg-muted rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="site-settings-tab">
      {/* Header */}
      <Card className="rounded-2xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-primary" />
                Site Settings
              </CardTitle>
              <CardDescription>
                Configure your store settings, shipping, taxes, and features
              </CardDescription>
            </div>
            <Button 
              onClick={saveSettings} 
              disabled={saving}
              className="rounded-full"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save All Settings
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Settings Navigation */}
      <Tabs value={activeSection} onValueChange={setActiveSection} className="space-y-6">
        <TabsList className="bg-muted/50 p-1 rounded-xl flex-wrap">
          <TabsTrigger value="business" className="rounded-lg gap-2">
            <Building2 className="h-4 w-4" /> Business
          </TabsTrigger>
          <TabsTrigger value="seo" className="rounded-lg gap-2">
            <Search className="h-4 w-4" /> SEO
          </TabsTrigger>
          <TabsTrigger value="shipping" className="rounded-lg gap-2">
            <Truck className="h-4 w-4" /> Shipping
          </TabsTrigger>
          <TabsTrigger value="tax" className="rounded-lg gap-2">
            <FileText className="h-4 w-4" /> Tax
          </TabsTrigger>
          <TabsTrigger value="payments" className="rounded-lg gap-2">
            <CreditCard className="h-4 w-4" /> Payments
            {(paypalStatus?.configured || stripeStatus?.configured) && (
              <CheckCircle className="h-3 w-3 text-green-500" />
            )}
          </TabsTrigger>
          <TabsTrigger value="regional" className="rounded-lg gap-2">
            <Globe className="h-4 w-4" /> Regional
          </TabsTrigger>
          <TabsTrigger value="features" className="rounded-lg gap-2">
            <ToggleLeft className="h-4 w-4" /> Features
          </TabsTrigger>
          <TabsTrigger value="storage" className="rounded-lg gap-2">
            <Cloud className="h-4 w-4" /> Storage
            {cloudinaryStatus?.configured && (
              <CheckCircle className="h-3 w-3 text-green-500" />
            )}
          </TabsTrigger>
        </TabsList>

        {/* Business Info */}
        <TabsContent value="business">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="text-lg">Business Information</CardTitle>
              <CardDescription>Your business details displayed across the site</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Business Name</Label>
                  <Input
                    value={settings.business_name}
                    onChange={(e) => updateSetting("business_name", e.target.value)}
                    placeholder="Your Business Name"
                    data-testid="business-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tagline</Label>
                  <Input
                    value={settings.tagline}
                    onChange={(e) => updateSetting("tagline", e.target.value)}
                    placeholder="Your business tagline"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="email"
                      value={settings.contact_email}
                      onChange={(e) => updateSetting("contact_email", e.target.value)}
                      className="pl-10"
                      placeholder="contact@example.com"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Contact Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={settings.contact_phone}
                      onChange={(e) => updateSetting("contact_phone", e.target.value)}
                      className="pl-10"
                      placeholder="+61 2 1234 5678"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>ABN</Label>
                  <Input
                    value={settings.abn}
                    onChange={(e) => updateSetting("abn", e.target.value)}
                    placeholder="12 345 678 901"
                  />
                </div>
              </div>

              <Separator />

              <div>
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Business Address
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label>Street Address</Label>
                    <Input
                      value={settings.address.street}
                      onChange={(e) => updateSetting("address.street", e.target.value)}
                      placeholder="123 Art Street"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={settings.address.city}
                      onChange={(e) => updateSetting("address.city", e.target.value)}
                      placeholder="Sydney"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Select 
                      value={settings.address.state} 
                      onValueChange={(v) => updateSetting("address.state", v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NSW">New South Wales</SelectItem>
                        <SelectItem value="VIC">Victoria</SelectItem>
                        <SelectItem value="QLD">Queensland</SelectItem>
                        <SelectItem value="WA">Western Australia</SelectItem>
                        <SelectItem value="SA">South Australia</SelectItem>
                        <SelectItem value="TAS">Tasmania</SelectItem>
                        <SelectItem value="NT">Northern Territory</SelectItem>
                        <SelectItem value="ACT">Australian Capital Territory</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Postcode</Label>
                    <Input
                      value={settings.address.postcode}
                      onChange={(e) => updateSetting("address.postcode", e.target.value)}
                      placeholder="2000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Input
                      value={settings.address.country}
                      onChange={(e) => updateSetting("address.country", e.target.value)}
                      placeholder="Australia"
                    />
                  </div>
                </div>
              </div>
              <Separator />

              <div>
                <h4 className="font-medium mb-4">Social Media Links</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  These URLs are shown in the site header and footer. Leave a field blank to hide that icon.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Instagram URL</Label>
                    <Input
                      value={settings.social?.instagram || ""}
                      onChange={(e) => updateSetting("social.instagram", e.target.value)}
                      placeholder="https://www.instagram.com/yourhandle"
                      data-testid="social-instagram-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Facebook URL</Label>
                    <Input
                      value={settings.social?.facebook || ""}
                      onChange={(e) => updateSetting("social.facebook", e.target.value)}
                      placeholder="https://www.facebook.com/yourpage"
                      data-testid="social-facebook-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>LinkedIn URL</Label>
                    <Input
                      value={settings.social?.linkedin || ""}
                      onChange={(e) => updateSetting("social.linkedin", e.target.value)}
                      placeholder="https://linkedin.com/company/yourcompany"
                      data-testid="social-linkedin-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>YouTube URL</Label>
                    <Input
                      value={settings.social?.youtube || ""}
                      onChange={(e) => updateSetting("social.youtube", e.target.value)}
                      placeholder="https://youtube.com/@yourchannel"
                      data-testid="social-youtube-input"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SEO Settings */}
        <TabsContent value="seo">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="text-lg">Search Engine Optimization</CardTitle>
              <CardDescription>Improve your site's visibility in search results</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Meta Title</Label>
                <Input
                  value={settings.seo.meta_title}
                  onChange={(e) => updateSetting("seo.meta_title", e.target.value)}
                  placeholder="Your Site Title | Brand"
                  data-testid="seo-title-input"
                />
                <p className="text-xs text-muted-foreground">
                  {settings.seo.meta_title.length}/60 characters (recommended)
                </p>
              </div>

              <div className="space-y-2">
                <Label>Meta Description</Label>
                <Textarea
                  value={settings.seo.meta_description}
                  onChange={(e) => updateSetting("seo.meta_description", e.target.value)}
                  placeholder="Describe your site in 155 characters..."
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  {settings.seo.meta_description.length}/155 characters (recommended)
                </p>
              </div>

              <div className="space-y-2">
                <Label>Keywords</Label>
                <Input
                  value={settings.seo.keywords}
                  onChange={(e) => updateSetting("seo.keywords", e.target.value)}
                  placeholder="keyword1, keyword2, keyword3"
                />
                <p className="text-xs text-muted-foreground">
                  Comma-separated keywords for search engines
                </p>
              </div>

              <div className="space-y-2">
                <Label>Social Share Image URL</Label>
                <Input
                  value={settings.seo.og_image}
                  onChange={(e) => updateSetting("seo.og_image", e.target.value)}
                  placeholder="https://..."
                />
                <p className="text-xs text-muted-foreground">
                  Image shown when site is shared on social media (1200x630 recommended)
                </p>
              </div>

              <div className="p-4 bg-muted/30 rounded-xl">
                <h5 className="font-medium mb-2">Search Preview</h5>
                <div className="p-3 bg-background rounded-lg border">
                  <p className="text-blue-600 text-lg hover:underline cursor-pointer">
                    {settings.seo.meta_title || "Your Page Title"}
                  </p>
                  <p className="text-green-700 text-sm">
                    arawol.art › collection
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {settings.seo.meta_description || "Your page description will appear here..."}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Shipping Settings */}
        <TabsContent value="shipping">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="text-lg">Shipping Configuration</CardTitle>
              <CardDescription>Set up shipping rates and options</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Free Shipping Threshold (AUD)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      value={settings.shipping.free_shipping_threshold}
                      onChange={(e) => updateSetting("shipping.free_shipping_threshold", parseFloat(e.target.value))}
                      className="pl-10"
                      placeholder="500"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Orders above this amount get free domestic shipping
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Domestic Flat Rate (AUD)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      value={settings.shipping.domestic_flat_rate}
                      onChange={(e) => updateSetting("shipping.domestic_flat_rate", parseFloat(e.target.value))}
                      className="pl-10"
                      placeholder="15"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Express Shipping Rate (AUD)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      value={settings.shipping.express_rate}
                      onChange={(e) => updateSetting("shipping.express_rate", parseFloat(e.target.value))}
                      className="pl-10"
                      placeholder="35"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Handling Time (days)</Label>
                  <Input
                    type="number"
                    value={settings.shipping.handling_days}
                    onChange={(e) => updateSetting("shipping.handling_days", parseInt(e.target.value))}
                    placeholder="2"
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">International Shipping</h4>
                    <p className="text-sm text-muted-foreground">Enable shipping to international destinations</p>
                  </div>
                  <Switch
                    checked={settings.shipping.international_enabled}
                    onCheckedChange={(v) => updateSetting("shipping.international_enabled", v)}
                  />
                </div>

                {settings.shipping.international_enabled && (
                  <div className="space-y-2">
                    <Label>International Flat Rate (AUD)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="number"
                        value={settings.shipping.international_rate}
                        onChange={(e) => updateSetting("shipping.international_rate", parseFloat(e.target.value))}
                        className="pl-10"
                        placeholder="75"
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tax Settings */}
        <TabsContent value="tax">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="text-lg">Tax Configuration</CardTitle>
              <CardDescription>Configure GST and tax settings for your store</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
                <div>
                  <h4 className="font-medium">GST Enabled</h4>
                  <p className="text-sm text-muted-foreground">Apply Goods and Services Tax to orders</p>
                </div>
                <Switch
                  checked={settings.tax.gst_enabled}
                  onCheckedChange={(v) => updateSetting("tax.gst_enabled", v)}
                />
              </div>

              {settings.tax.gst_enabled && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>GST Rate (%)</Label>
                      <Input
                        type="number"
                        value={settings.tax.gst_rate}
                        onChange={(e) => updateSetting("tax.gst_rate", parseFloat(e.target.value))}
                        placeholder="10"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
                    <div>
                      <h4 className="font-medium">Prices Include GST</h4>
                      <p className="text-sm text-muted-foreground">Display prices with GST already included</p>
                    </div>
                    <Switch
                      checked={settings.tax.gst_inclusive}
                      onCheckedChange={(v) => updateSetting("tax.gst_inclusive", v)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
                    <div>
                      <h4 className="font-medium">ABN Exemption</h4>
                      <p className="text-sm text-muted-foreground">Allow wholesale customers with ABN to be GST exempt</p>
                    </div>
                    <Switch
                      checked={settings.tax.abn_exempt}
                      onCheckedChange={(v) => updateSetting("tax.abn_exempt", v)}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Settings */}
        <TabsContent value="payments">
          <div className="space-y-6">
            {/* PayPal Configuration */}
            <Card className="rounded-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#003087] rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-xs">PP</span>
                      </div>
                      PayPal Configuration
                    </CardTitle>
                    <CardDescription>Configure PayPal for checkout and Pay Later options</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {paypalStatus?.working ? (
                      <Badge className="bg-green-500 text-white">
                        <CheckCircle className="h-3 w-3 mr-1" /> Working
                      </Badge>
                    ) : paypalStatus?.configured ? (
                      <Badge variant="outline" className="text-amber-600 border-amber-600">
                        <XCircle className="h-3 w-3 mr-1" /> Configured (Not Working)
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-600">
                        <XCircle className="h-3 w-3 mr-1" /> Not Configured
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl text-sm">
                  <p className="font-medium text-blue-700 dark:text-blue-300 mb-2">How to get PayPal credentials:</p>
                  <ol className="list-decimal list-inside text-blue-600 dark:text-blue-400 space-y-1">
                    <li>Go to <a href="https://developer.paypal.com/dashboard/" target="_blank" rel="noopener noreferrer" className="underline">PayPal Developer Dashboard</a></li>
                    <li>Log in with your PayPal account</li>
                    <li>Go to Apps & Credentials</li>
                    <li>Create an app or select existing one</li>
                    <li>Copy Client ID and Secret</li>
                  </ol>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Client ID</Label>
                    <Input
                      value={settings.paypal?.client_id || ""}
                      onChange={(e) => updateSetting("paypal.client_id", e.target.value)}
                      placeholder="Enter PayPal Client ID"
                      data-testid="paypal-client-id"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Client Secret</Label>
                    <Input
                      type="password"
                      value={settings.paypal?.client_secret || ""}
                      onChange={(e) => updateSetting("paypal.client_secret", e.target.value)}
                      placeholder="Enter PayPal Client Secret"
                      data-testid="paypal-client-secret"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
                  <div className="space-y-1">
                    <Label className="text-base">Sandbox Mode</Label>
                    <p className="text-sm text-muted-foreground">Use sandbox for testing (disable for live payments)</p>
                  </div>
                  <Switch
                    checked={settings.paypal?.sandbox_mode ?? true}
                    onCheckedChange={(v) => updateSetting("paypal.sandbox_mode", v)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
                  <div className="space-y-1">
                    <Label className="text-base">Pay Later / Pay in 4</Label>
                    <p className="text-sm text-muted-foreground">Enable PayPal's Buy Now Pay Later options</p>
                  </div>
                  <Switch
                    checked={settings.paypal?.pay_later_enabled ?? true}
                    onCheckedChange={(v) => updateSetting("paypal.pay_later_enabled", v)}
                  />
                </div>

                <div className="flex gap-3">
                  <Button 
                    onClick={() => savePaymentCredentials('paypal')}
                    disabled={saving}
                    className="rounded-full"
                  >
                    {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save PayPal Settings
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={testPayPalConnection}
                    className="rounded-full"
                    disabled={!settings.paypal?.client_id}
                  >
                    Test Connection
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Stripe Configuration */}
            <Card className="rounded-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#635BFF] rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-xs">S</span>
                      </div>
                      Stripe Configuration
                    </CardTitle>
                    <CardDescription>Configure Stripe for credit card payments</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {stripeStatus?.working ? (
                      <Badge className="bg-green-500 text-white">
                        <CheckCircle className="h-3 w-3 mr-1" /> Working
                      </Badge>
                    ) : stripeStatus?.configured ? (
                      <Badge variant="outline" className="text-amber-600 border-amber-600">
                        <XCircle className="h-3 w-3 mr-1" /> Configured (Not Working)
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-600">
                        <XCircle className="h-3 w-3 mr-1" /> Not Configured
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-xl text-sm">
                  <p className="font-medium text-purple-700 dark:text-purple-300 mb-2">How to get Stripe credentials:</p>
                  <ol className="list-decimal list-inside text-purple-600 dark:text-purple-400 space-y-1">
                    <li>Go to <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline">Stripe Dashboard API Keys</a></li>
                    <li>Copy your Publishable key and Secret key</li>
                    <li>For testing, use keys from "Test mode"</li>
                  </ol>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Publishable Key</Label>
                    <Input
                      value={settings.stripe?.publishable_key || ""}
                      onChange={(e) => updateSetting("stripe.publishable_key", e.target.value)}
                      placeholder={stripeStatus?.configured ? "Keys saved — enter new key to update" : "pk_test_..."}
                      data-testid="stripe-publishable-key"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Secret Key</Label>
                    <Input
                      type="password"
                      value={settings.stripe?.secret_key || ""}
                      onChange={(e) => updateSetting("stripe.secret_key", e.target.value)}
                      placeholder={stripeStatus?.configured ? "Keys saved — enter new key to update" : "sk_test_..."}
                      data-testid="stripe-secret-key"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
                  <div className="space-y-1">
                    <Label className="text-base">Test Mode</Label>
                    <p className="text-sm text-muted-foreground">Use test keys (disable for live payments)</p>
                  </div>
                  <Switch
                    checked={settings.stripe?.test_mode ?? true}
                    onCheckedChange={(v) => updateSetting("stripe.test_mode", v)}
                  />
                </div>

                <Button 
                  onClick={() => savePaymentCredentials('stripe')}
                  disabled={saving}
                  className="rounded-full"
                >
                  {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Stripe Settings
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Regional Settings */}
        <TabsContent value="regional">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="text-lg">Regional Settings</CardTitle>
              <CardDescription>Currency, timezone, and localization preferences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select 
                    value={settings.regional.currency} 
                    onValueChange={(v) => updateSetting("regional.currency", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AUD">Australian Dollar (AUD)</SelectItem>
                      <SelectItem value="USD">US Dollar (USD)</SelectItem>
                      <SelectItem value="EUR">Euro (EUR)</SelectItem>
                      <SelectItem value="GBP">British Pound (GBP)</SelectItem>
                      <SelectItem value="NZD">New Zealand Dollar (NZD)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Currency Symbol</Label>
                  <Input
                    value={settings.regional.currency_symbol}
                    onChange={(e) => updateSetting("regional.currency_symbol", e.target.value)}
                    placeholder="$"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select 
                    value={settings.regional.timezone} 
                    onValueChange={(v) => updateSetting("regional.timezone", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Australia/Sydney">Sydney (AEST/AEDT)</SelectItem>
                      <SelectItem value="Australia/Melbourne">Melbourne (AEST/AEDT)</SelectItem>
                      <SelectItem value="Australia/Brisbane">Brisbane (AEST)</SelectItem>
                      <SelectItem value="Australia/Perth">Perth (AWST)</SelectItem>
                      <SelectItem value="Australia/Adelaide">Adelaide (ACST/ACDT)</SelectItem>
                      <SelectItem value="Pacific/Auckland">Auckland (NZST/NZDT)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Date Format</Label>
                  <Select 
                    value={settings.regional.date_format} 
                    onValueChange={(v) => updateSetting("regional.date_format", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DD/MM/YYYY">DD/MM/YYYY (31/12/2025)</SelectItem>
                      <SelectItem value="MM/DD/YYYY">MM/DD/YYYY (12/31/2025)</SelectItem>
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD (2025-12-31)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Feature Toggles */}
        <TabsContent value="features">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="text-lg">Feature Toggles</CardTitle>
              <CardDescription>Enable or disable site features</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: "wholesale_enabled", label: "Wholesale Program", desc: "Allow businesses to apply for wholesale accounts" },
                { key: "promoter_program_enabled", label: "Ambassador/Promoter Program", desc: "Enable referral commissions for promoters" },
                { key: "sales_agent_program_enabled", label: "Sales Agent Program", desc: "Allow sales agents to onboard wholesale customers" },
                { key: "dynamic_pricing_enabled", label: "Dynamic Pricing", desc: "Automatically adjust prices based on stock levels" },
                { key: "certificates_enabled", label: "Certificates of Authenticity", desc: "Issue digital certificates for artworks" },
                { key: "ar_viewer_enabled", label: "AR Viewer", desc: "Allow customers to view art on their walls" },
                { key: "frame_configurator_enabled", label: "Frame Configurator", desc: "Let customers customize framing options" },
              ].map((feature) => (
                <div 
                  key={feature.key} 
                  className="flex items-center justify-between p-4 bg-muted/30 rounded-xl"
                >
                  <div>
                    <h4 className="font-medium">{feature.label}</h4>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={settings.features[feature.key] ? "default" : "secondary"}>
                      {settings.features[feature.key] ? "Enabled" : "Disabled"}
                    </Badge>
                    <Switch
                      checked={settings.features[feature.key]}
                      onCheckedChange={(v) => updateSetting(`features.${feature.key}`, v)}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cloud Storage Settings */}
        <TabsContent value="storage">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Cloud className="h-5 w-5" />
                Cloud Image Storage
              </CardTitle>
              <CardDescription>
                Configure Cloudinary for persistent image storage. Images uploaded here won't be lost between deployments.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status Banner */}
              <div className={`p-4 rounded-xl flex items-center gap-3 ${
                cloudinaryStatus?.configured 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-amber-50 border border-amber-200'
              }`}>
                {cloudinaryStatus?.configured ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">Cloudinary Connected</p>
                      <p className="text-sm text-green-600">
                        Cloud: {cloudinaryStatus?.cloud_name} • Images will be stored in the cloud
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800">Cloudinary Not Configured</p>
                      <p className="text-sm text-amber-600">
                        Images are stored locally and may be lost between deployments
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Setup Instructions */}
              <div className="p-4 bg-muted/30 rounded-xl">
                <h4 className="font-medium mb-2">How to get Cloudinary credentials:</h4>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Go to <a href="https://cloudinary.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">cloudinary.com</a> and create a free account</li>
                  <li>Open your Dashboard</li>
                  <li>Copy the Cloud Name, API Key, and API Secret</li>
                  <li>Paste them below and save</li>
                </ol>
                <p className="text-xs text-muted-foreground mt-2">
                  Free tier includes 25GB storage and 25GB bandwidth/month
                </p>
              </div>

              {/* Credential Fields */}
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label>Cloud Name</Label>
                  <Input
                    value={settings.cloudinary?.cloud_name || ""}
                    onChange={(e) => updateSetting("cloudinary.cloud_name", e.target.value)}
                    placeholder="your-cloud-name"
                    data-testid="cloudinary-cloud-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input
                    value={settings.cloudinary?.api_key || ""}
                    onChange={(e) => updateSetting("cloudinary.api_key", e.target.value)}
                    placeholder="123456789012345"
                    data-testid="cloudinary-api-key"
                  />
                </div>
                <div className="space-y-2">
                  <Label>API Secret</Label>
                  <Input
                    type="password"
                    value={settings.cloudinary?.api_secret || ""}
                    onChange={(e) => updateSetting("cloudinary.api_secret", e.target.value)}
                    placeholder="••••••••••••••••"
                    data-testid="cloudinary-api-secret"
                  />
                  <p className="text-xs text-muted-foreground">
                    This is kept secure and never exposed to the frontend
                  </p>
                </div>
              </div>

              <Button 
                onClick={saveSettings}
                disabled={saving}
                className="rounded-full"
              >
                {saving ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Cloudinary Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SiteSettingsTab;
